import React from "react"
import PropTypes from "prop-types"
import moment from "moment"

import { ButtonRow, DatePickerRange, Header, HeaderInput, TopBar } from "quorumdesign"

import { StyledGlobalDateDropdown } from "app/static/frontend/dashboards/components/DashboardHeader/StyledGlobalDateDropdown"

// action creators
import {
    archiveDashboard,
    globalDateButtonOnClick,
    refreshDashboard,
    saveDashboard,
    setOrRemoveDashboardAsHomepage,
} from "app/static/frontend/dashboards/action-creators/dashboardHeaderActionCreators"

// helpers
import { saveElementAsImage } from "app/static/frontend/imports/desktopHelperFunctions"

// constants
import { dashboardPaths } from "app/static/frontend/imports/desktopPaths"

export const DashboardHeader = (props) => {
    const standardButtonOptions = []
    const shouldShowDateRangeButton =
        props.widgets &&
        Boolean(Object.keys(props.widgets).length) &&
        (!props.isExternal || (props.isExternal && props.globalDate))
    const shouldShowAllDashboardsButton = !props.isExternal && props.dashboardHomepage
    const shouldShowDownloadButton = !props.isExternal || (props.isExternal && !props.externalDownloadsDisabled)
    const shouldShowEditSaveButton =
        !props.isExternal &&
        (Userdata.is_admin || !Object.values(Userdata.persona_dashboards).includes(parseInt(props.id)))
    const shouldShowArchiveButton = !props.isExternal && !Object.values(Userdata.persona_dashboards).includes(props.id)

    if (shouldShowDateRangeButton) {
        const siblingElements = []

        if (props.globalDateShouldRenderCustomDatePicker) {
            siblingElements.push(
                <DatePickerRange
                    applyOnClick={async (valueFromState, valueToState) => {
                        const valueFromStateMoment = moment(valueFromState)
                        const valueToStateMoment = moment(valueToState)

                        if (valueFromStateMoment.isValid() && valueToStateMoment.isValid()) {
                            globalDateButtonOnClick({
                                globalDateCustomFrom: moment(valueFromState),
                                globalDateCustomTo: moment(valueToState),

                                createOrUpdateDashboard: props.createOrUpdateDashboard,
                                destroyDashboardStore: props.destroyDashboardStore,
                                deletedWidgets: props.deletedWidgets,
                                id: props.id,
                                name: props.name,
                                sendAction: props.sendAction,
                                setDashboardsValues: props.setDashboardsValues,
                                widgets: props.widgets,
                            })

                            props.setDashboardsValues({
                                id: props.id,
                                values: {
                                    globalDateShouldRenderCustomDatePicker: false,
                                },
                            })
                        }
                    }}
                    cancelOnClick={() => {
                        props.setDashboardsValues({
                            id: props.id,
                            values: {
                                globalDateShouldRenderCustomDatePicker: false,
                            },
                        })
                    }}
                    isOpen
                    isRange
                    labelFrom="From"
                    labelTo="To"
                    // https://github.com/Hacker0x01/react-datepicker/issues/2376
                    // https://github.com/Hacker0x01/react-datepicker/issues/2930
                    // https://github.com/Hacker0x01/react-datepicker/issues/2872#issue-853667583
                    // "With minDate passed as null, the behavior is as expected.
                    // In this case the highlighted date does not follow when clicking through months."
                    minDate={null}
                    onClickOutside={() => {
                        props.setDashboardsValues({
                            id: props.id,
                            values: {
                                globalDateShouldRenderCustomDatePicker: false,
                            },
                        })
                    }}
                    valueFrom={
                        props.globalDateCustomFrom &&
                        props.globalDateCustomFrom.isValid() &&
                        props.globalDateCustomFrom.toDate()
                    }
                    valueTo={
                        props.globalDateCustomTo &&
                        props.globalDateCustomTo.isValid() &&
                        props.globalDateCustomTo.toDate()
                    }
                />,
            )
        }

        standardButtonOptions.push({
            component: "dropdown",
            parentElement: StyledGlobalDateDropdown,
            siblingElements: siblingElements,
            dataCy: "dashboards-global-date-dropdown-button",
            disabled:
                props.isExternal ||
                props.isSaving ||
                (!Userdata.is_admin && Object.values(Userdata.persona_dashboards).includes(parseInt(props.id))),
            dropdownBoxOffsetVertical: "4px",
            isBoxRightAlign: true,
            isTiny: true,
            leftIcon: "calendar-alt",
            leftIconFamily: "fas",
            leftIconSize: "sm",
            menuOptions: props.globalDateDropdownMenuOptions,
            text: props.globalDateButtonText,
            type: "ghost",
        })
    }

    if (shouldShowAllDashboardsButton) {
        standardButtonOptions.push({
            dataCy: "see-all-dashboards-button",
            icon: "dashboard",
            icon_family: "q",
            id: "see-all-dashboards-button",
            isTiny: true,
            onClick: () => {
                props.segueToUrl(dashboardPaths.dashboardPrefix)
            },
            text: "See All Dashboards",
            type: "ghost",
        })
    }

    if (shouldShowDownloadButton) {
        standardButtonOptions.push({
            dataCy: "dashboard-download-button",
            disabled: props.isLoadingDownload,
            id: "dashboard-download-button",
            icon: props.isLoadingDownload ? "spinner" : "download",
            iconSpin: props.isLoadingDownload,
            onClick: async () => {
                if (!props.isLoadingDownload) {
                    props.setDashboardsValues({
                        id: props.id,
                        values: {
                            isLoadingDownload: true,
                        },
                    })

                    // the computationally intensive saveElementAsImage blocks before React can rerender,
                    // so we want to rerender before we generate the download
                    await new Promise((r) => setTimeout(r, 10))

                    await saveElementAsImage({
                        element: document.getElementById(`dashboard-${props.id}`),
                        format: "png",
                        title: `dashboard-${props.id}`,
                    })

                    props.setDashboardsValues({
                        id: props.id,
                        values: {
                            isLoadingDownload: false,
                        },
                    })
                }
            },
            text: props.isLoadingDownload ? "Downloading..." : "Download",
            isTiny: true,
            type: "ghost",
        })
    }

    if (shouldShowEditSaveButton) {
        standardButtonOptions.push({
            dataCy: "dashboard-edit-save-button",
            disabled: props.isSaving,
            icon: props.editing ? "check" : "pencil-alt",
            isTiny: true,
            onClick: async () => {
                // if we are currently editing when the button is pressed,
                // that means that we are no longer editing and need to save
                if (props.editing) {
                    // if we segued back from the WidgetEditForm,
                    // we now want to remove the ?editing=true from the location URI
                    if (props.locationQuery) {
                        window.history.replaceState({}, document.title, window.location.pathname)
                    }

                    await saveDashboard({
                        createOrUpdateDashboard: props.createOrUpdateDashboard,
                        deletedWidgets: props.deletedWidgets,
                        id: props.id,
                        name: props.name,
                        sendAction: props.sendAction,
                        setDashboardsValues: props.setDashboardsValues,
                    })
                }
                // update the editing status in the store
                props.setDashboardsValues({
                    id: props.id,
                    values: {
                        ...// if we have deleted any widgets,
                        // we need to reset the prop which keeps track of deleted widgets
                        (props.deletedWidgets.length && { deletedWidgets: [] }),
                        editing: !props.editing,
                    },
                })

                // necessary because app/static/frontend/widgets/components/ListWidget.jsx
                // uses react-virtualized, which requires us to manually invoke
                // cache.clearAll() and list.recomputeRowHeights() whenever we need to
                // re-render the ListItems being rendered in the rowRenderer() function

                // we need this because we pass the editing prop down to the ListItem
                // in order to prevent the Anchor from being rendered
                // when we are editing or in an external Dashboard
                window.dispatchEvent(new Event("ListWidgetRememo"))
            },
            text: (props.isSaving && "Saving...") || (props.editing && "Save") || "Edit",
            type: props.editing ? "filled" : "outlined",
        })
    }

    const editingButtonOptions = [
        {
            dataCy: "dashboard-edit-new-widget-button",
            // if we are editing the Dashboard and have deleted a widget,
            // the user should first save their changes before they create a new widget
            disabled: props.isSaving,
            icon: "plus",
            isTiny: true,
            onClick: async () => {
                props.setDashboardsValues({
                    id: props.id,
                    values: {
                        creatingWidget: true,
                    },
                })

                await saveDashboard({
                    createOrUpdateDashboard: props.createOrUpdateDashboard,
                    deletedWidgets: props.deletedWidgets,
                    id: props.id,
                    name: props.name,
                    sendAction: props.sendAction,
                    setDashboardsValues: props.setDashboardsValues,
                })
            },
            text: (props.isSaving && "Saving...") || "New Widget",
            type: "outlined",
        },
        {
            disabled: props.isSaving,
            icon: "home",
            isTiny: true,
            onClick: () => {
                setOrRemoveDashboardAsHomepage({
                    id: props.id,
                    removeHomepage: props.isHomepageDashboard,
                    sendAction: props.sendAction,
                    setDashboardsValues: props.setDashboardsValues,
                    updateUserdata: props.updateUserdata,
                })
            },
            text: props.isHomepageDashboard ? "Remove Homepage" : "Make Homepage",
            type: "ghost",
        },
        {
            icon: "users",
            isTiny: true,
            onClick: () => {
                props.setDashboardsValues({
                    id: props.id,
                    values: {
                        editingTeamPermissions: true,
                    },
                })
            },
            text: "Permissions",
            type: "ghost",
        },
        {
            dataCy: "dashboard-refresh-button",
            disabled: props.isRefreshing,
            icon: "sync-alt",
            isTiny: true,
            onClick: () => {
                refreshDashboard({
                    id: props.id,
                    sendAction: props.sendAction,
                    setDashboardsValues: props.setDashboardsValues,
                })
                props.destroyDashboardStore({
                    id: props.id,
                })
            },
            text: "Refresh",
            type: "ghost",
        },
    ]

    if (shouldShowArchiveButton) {
        editingButtonOptions.push({
            icon: "archive",
            isTiny: true,
            onClick: () =>
                archiveDashboard({
                    createOrUpdateDashboard: props.createOrUpdateDashboard,
                    destroyDashboardStore: props.destroyDashboardStore,
                    id: props.id,
                    segueToUrl: props.segueToUrl,
                }),
            text: "Archive",
            type: "ghost",
        })
    }

    const buttonRowBreakpoints = () => {
        // when we're not editing there are less buttons
        // hardcoding this while waiting on input from design
        if (!props.editing) {
            return {
                iconResponsiveBreakpoint: "1400px",
                textResponsiveBreakpoint: "1250px",
            }
        }

        // length of window when topbar input stops shrinking + side nav
        const defaultBreakpointValue = 594
        // added values below are length of edit buttons that are always present
        let defaultIconBreakpointValue = defaultBreakpointValue + 522
        let defaultTextBreakpointValue = defaultBreakpointValue + 447

        // all below values are the length of the buttons (with/without icons) + 8px gap
        if (shouldShowDateRangeButton) {
            defaultIconBreakpointValue += 167
            defaultTextBreakpointValue += 145
        }

        if (shouldShowAllDashboardsButton) {
            defaultIconBreakpointValue += 173
            defaultTextBreakpointValue += 155
        }

        if (shouldShowDownloadButton) {
            defaultIconBreakpointValue += 113
            defaultTextBreakpointValue += 94
        }

        if (shouldShowEditSaveButton) {
            defaultIconBreakpointValue += 82
            defaultTextBreakpointValue += 64
        }

        if (shouldShowArchiveButton) {
            defaultIconBreakpointValue += 102
            defaultTextBreakpointValue += 79
        }

        return {
            iconResponsiveBreakpoint: `${defaultIconBreakpointValue}px`,
            textResponsiveBreakpoint: `${defaultTextBreakpointValue}px`,
        }
    }

    const { iconResponsiveBreakpoint, textResponsiveBreakpoint } = buttonRowBreakpoints()

    return (
        <Header>
            <TopBar
                hasGreyBackground={props.isExternal}
                onClickBack={!props.isExternal && !props.dashboardHomepage && (() => props.popPage())}
                shouldTruncateTitle={!props.editing}
                title={
                    props.editing ? (
                        <HeaderInput
                            onChange={(value) => {
                                props.setDashboardsValues({
                                    id: props.id,
                                    values: {
                                        name: value,
                                    },
                                })
                            }}
                            placeholder="Enter your new dashboard name here..."
                            value={props.name}
                        />
                    ) : (
                        props.name
                    )
                }
                titleImage={props.dashboardHomepage && Userdata.organization_logo_color_url}
                titleImageDataCy="dashboard-organization-logo"
            >
                {props.editing && (
                    <ButtonRow
                        buttonOptions={editingButtonOptions}
                        iconResponsiveBreakpoint={iconResponsiveBreakpoint}
                        textResponsiveBreakpoint={textResponsiveBreakpoint}
                    />
                )}
                <ButtonRow
                    buttonOptions={standardButtonOptions}
                    iconResponsiveBreakpoint={iconResponsiveBreakpoint}
                    textResponsiveBreakpoint={textResponsiveBreakpoint}
                />
            </TopBar>
        </Header>
    )
}

DashboardHeader.propTypes = {
    // inherited from Index.jsx
    id: PropTypes.string,

    // passed from LandingPageOrDashboard
    dashboardHomepage: PropTypes.bool,

    // action creators
    createOrUpdateDashboard: PropTypes.func,
    destroyDashboardStore: PropTypes.func,
    locationQuery: PropTypes.object,
    popPage: PropTypes.func,
    segueToUrl: PropTypes.func,
    sendAction: PropTypes.func,
    setDashboardsValues: PropTypes.func,
    updateUserdata: PropTypes.func,

    // store
    deletedWidgets: PropTypes.array,
    editing: PropTypes.bool,
    externalDownloadsDisabled: PropTypes.bool,
    globalDate: PropTypes.oneOfType([
        // custom date
        PropTypes.string,
        // DjangIO.app.events.models.DateRangeType
        PropTypes.number,
    ]),
    globalDateButtonText: PropTypes.string,
    globalDateCustomFrom: PropTypes.instanceOf(moment),
    globalDateCustomFromInitial: PropTypes.instanceOf(moment),
    globalDateCustomTo: PropTypes.instanceOf(moment),
    globalDateCustomToInitial: PropTypes.instanceOf(moment),
    globalDateDropdownMenuOptions: PropTypes.array,
    globalDateShouldRenderCustomDatePicker: PropTypes.bool,
    isExternal: PropTypes.bool,
    isHomepageDashboard: PropTypes.bool,
    isLoadingDownload: PropTypes.bool,
    isRefreshing: PropTypes.bool,
    isSaving: PropTypes.bool,
    name: PropTypes.string,
    widgets: PropTypes.object,
}

export default DashboardHeader
